import React, { useCallback, useEffect, useRef, useState } from 'react'
import { App, Button, Card, DatePicker, Descriptions, Image, Modal, Select, Space, Statistic, Table, Tag, Typography, Upload } from 'antd'
import { CameraOutlined, DownloadOutlined, EyeOutlined, FileTextOutlined, ReloadOutlined, ScanOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import type { UploadFile } from 'antd/es/upload/interface'
import dayjs, { type Dayjs } from 'dayjs'
import { sellerOrdersAPI } from '../utils/sellerApi'
import { formatSellerCurrency, useSellerCurrencyStore } from '../utils/currency'
import { useSellerAuth } from '../contexts/SellerAuthContext'
import { scanQRFrame } from '../utils/qrScanner'

interface Customer {
  id: number
  name: string
  email: string
}

interface LaravelPaginator<T> {
  current_page: number
  data: T[]
  per_page: number
  total: number
  last_page: number
}

interface OrderItem {
  id: number
  product_name: string
  product_sku: string
  quantity: number
  unit_price: number
  total_price: number
}

interface PackageImage {
  id: number
  url?: string | null
  label?: string | null
  s3_key?: string | null
}

interface Order {
  id: number
  order_number: string
  status: string
  payment_status: string
  total_amount: number | string
  tracking_id?: string | null
  tracking_number?: string | null
  shipping_address?: Record<string, unknown> | null
  formatted_shipping_address?: string | null
  created_at: string
  customer?: Customer | null
  items: OrderItem[]
  package_images?: PackageImage[]
}

interface OrderSummary {
  total_orders: number
  revenue: number
  today_revenue: number
  pending: number
  shipped: number
  delivered: number
  cancelled: number
}

interface DateRangePayload {
  start_date?: string | null
  end_date?: string | null
}

const PACKAGE_PHOTO_LABELS = ['front', 'back', 'side', 'memo'] as const

const paymentStatusColors: Record<string, string> = {
  paid: 'green',
  failed: 'red',
  refunded: 'purple',
  pending: 'orange',
}

const formatLabel = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const statusOptions = ['pending', 'confirmed', 'processing', 'packed', 'shipped', 'delivered', 'cancelled']
const csvStatusOptions = ['all', ...statusOptions]

const statusColors: Record<string, string> = {
  pending: 'default',
  confirmed: 'blue',
  processing: 'gold',
  packed: 'purple',
  shipped: 'cyan',
  delivered: 'green',
  cancelled: 'red',
}

const emptySummary: OrderSummary = {
  total_orders: 0,
  revenue: 0,
  today_revenue: 0,
  pending: 0,
  shipped: 0,
  delivered: 0,
  cancelled: 0,
}

const toNumber = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

const deriveSummaryFromOrders = (orders: Order[]): OrderSummary => {
  const deliveredOrders = orders.filter((order) => order.status === 'delivered')

  return {
    total_orders: orders.length,
    revenue: deliveredOrders.reduce((total, order) => total + toNumber(order.total_amount), 0),
    today_revenue: 0,
    pending: orders.filter((order) => ['pending', 'confirmed'].includes(order.status)).length,
    shipped: orders.filter((order) => order.status === 'shipped').length,
    delivered: deliveredOrders.length,
    cancelled: orders.filter((order) => order.status === 'cancelled').length,
  }
}

const normalizeSummary = (rawSummary: unknown, ordersFallback: Order[]): OrderSummary => {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return deriveSummaryFromOrders(ordersFallback)
  }

  const source = rawSummary as Record<string, unknown>

  return {
    total_orders: toNumber(source.total_orders),
    revenue: toNumber(source.revenue),
    today_revenue: toNumber(source.today_revenue),
    pending: toNumber(source.pending),
    shipped: toNumber(source.shipped),
    delivered: toNumber(source.delivered),
    cancelled: toNumber(source.cancelled),
  }
}

const formatCurrency = (value: number) =>
  formatSellerCurrency(Number(value ?? 0), { fromInr: true })

const formatDateLabel = (value?: string | null) => {
  if (!value) {
    return 'N/A'
  }

  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format('DD MMM YYYY') : value
}

const iconLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
}

const iconValueStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  color: '#334155',
}

const readAddressField = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = record[key]

    if (value === undefined || value === null) {
      continue
    }

    const text = String(value).trim()
    if (text !== '') {
      return text
    }
  }

  return null
}

const resolveAddressRecord = (rawAddress: unknown): Record<string, unknown> | null => {
  if (!rawAddress) {
    return null
  }

  if (typeof rawAddress === 'object' && !Array.isArray(rawAddress)) {
    return rawAddress as Record<string, unknown>
  }

  if (typeof rawAddress === 'string') {
    try {
      const parsed = JSON.parse(rawAddress)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
    } catch {
      return null
    }
  }

  return null
}

const formatOrderShippingAddress = (order: Order) => {
  const record = resolveAddressRecord(order.shipping_address)

  if (!record) {
    const fallback = order.formatted_shipping_address?.trim()
    if (fallback) {
      return {
        name: null,
        contact: null,
        lines: fallback.split('\n').map((line) => line.trim()).filter(Boolean),
        notes: null,
      }
    }

    return {
      name: null,
      contact: null,
      lines: [],
      notes: null,
    }
  }

  const name = readAddressField(record, ['full_name', 'name', 'fullName'])
  const email = readAddressField(record, ['email'])
  const phone = readAddressField(record, ['phone', 'phone_number', 'mobile'])

  const line1 = readAddressField(record, ['address_line_1', 'address1', 'line1', 'street', 'address'])
  const line2 = readAddressField(record, ['address_line_2', 'address2', 'line2'])
  const city = readAddressField(record, ['city'])
  const state = readAddressField(record, ['state', 'province', 'region'])
  const postal = readAddressField(record, ['postal_code', 'zip', 'zip_code', 'pincode', 'postcode'])
  const country = readAddressField(record, ['country'])
  const notes = readAddressField(record, ['customer_notes', 'notes'])

  const locality = [city, state, postal].filter(Boolean).join(', ')
  const lines = [line1, line2, locality || null, country].filter((value): value is string => Boolean(value))
  const contact = [phone, email].filter(Boolean).join(' | ') || null

  return {
    name,
    contact,
    lines,
    notes,
  }
}

const parseScannedCode = (raw: string): { type: 'qr_order'; orderId: number } | { type: 'barcode'; orderNumber: string } | null => {
  const trimmed = raw.trim()
  if (!trimmed) {
    return null
  }

  try {
    const parsed = JSON.parse(trimmed) as { type?: string; order_id?: unknown; order_number?: unknown }
    const parsedOrderId = Number(parsed.order_id)

    if (parsed.type === 'trendify_order' && Number.isFinite(parsedOrderId)) {
      return { type: 'qr_order', orderId: parsedOrderId }
    }
  } catch {
    // Not JSON — may be barcode or URL.
  }

  if (trimmed.startsWith('http')) {
    try {
      const url = new URL(trimmed)
      const parts = url.pathname.split('/')
      const orderIndex = parts.indexOf('orders')
      const parsedOrderId = Number(parts[orderIndex + 1])

      if (Number.isFinite(parsedOrderId)) {
        return { type: 'qr_order', orderId: parsedOrderId }
      }
    } catch {
      return null
    }
  }

  return { type: 'barcode', orderNumber: trimmed }
}

const OrdersPage = () => {
  const activeCurrency = useSellerCurrencyStore((state) => state.currency)
  const [orders, setOrders] = useState<Order[]>([])
  const [summary, setSummary] = useState<OrderSummary>(emptySummary)
  const [apiDateRange, setApiDateRange] = useState<DateRangePayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [csvStatusFilter, setCsvStatusFilter] = useState<string>('all')
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().subtract(6, 'day'),
    dayjs(),
  ])
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [modalVisible, setModalVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [scannerOpen, setScannerOpen] = useState(false)
  const [barcodeReaderOpen, setBarcodeReaderOpen] = useState(false)
  const [barcodeReaderListening, setBarcodeReaderListening] = useState(false)
  const [scanUpdateOpen, setScanUpdateOpen] = useState(false)
  const [scannedBarcode, setScannedBarcode] = useState<string | null>(null)
  const [scanTargetStatus, setScanTargetStatus] = useState<string>('processing')
  const [packedModalOpen, setPackedModalOpen] = useState(false)
  const [packedPhotoFiles, setPackedPhotoFiles] = useState<UploadFile[]>([])
  const [pendingPackedOrderId, setPendingPackedOrderId] = useState<number | null>(null)
  const [pendingPackedViaScan, setPendingPackedViaScan] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const barcodeBufferRef = useRef('')
  const barcodeLastKeyAtRef = useRef(0)
  const barcodeArmTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canUpdateOrderStatus = hasPermission('orders.update')

  const downloadOrderMemo = async (orderId: number, orderNumber: string) => {
    try {
      const response = await sellerOrdersAPI.downloadMemo(orderId)
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `order-memo-${orderNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      message.success('Order memo downloaded. Print and attach to package.')
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to download order memo')
    }
  }

  const openOrder = useCallback(async (orderId: number) => {
    setSaving(true)
    try {
      const response = await sellerOrdersAPI.get(orderId)
      setSelectedOrder(response.data.data)
      setModalVisible(true)
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load order details')
    } finally {
      setSaving(false)
    }
  }, [message])

  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
  }, [])

  const stopBarcodeReaderListening = useCallback(() => {
    setBarcodeReaderListening(false)
    setBarcodeReaderOpen(false)
    barcodeBufferRef.current = ''
    barcodeLastKeyAtRef.current = 0
    if (barcodeArmTimeoutRef.current) {
      clearTimeout(barcodeArmTimeoutRef.current)
      barcodeArmTimeoutRef.current = null
    }
  }, [])

  const startScanner = useCallback(async () => {
    setCameraError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
        },
      })

      streamRef.current = stream

      if (videoRef.current) {
        videoRef.current.srcObject = stream
        await videoRef.current.play()
      }

      const scanCanvas = document.createElement('canvas')
      const ctx = scanCanvas.getContext('2d')

      if (!ctx) {
        setCameraError('Unable to start the QR scanner on this browser.')
        return
      }

      scanIntervalRef.current = setInterval(() => {
        if (!videoRef.current || videoRef.current.readyState < 2) {
          return
        }

        scanCanvas.width = videoRef.current.videoWidth
        scanCanvas.height = videoRef.current.videoHeight
        ctx.drawImage(videoRef.current, 0, 0)

        const result = scanQRFrame(scanCanvas)
        if (!result) {
          return
        }

        try {
          const parsed = JSON.parse(result) as { type?: string; order_id?: unknown }
          const parsedOrderId = Number(parsed.order_id)

          if (parsed.type === 'trendify_order' && Number.isFinite(parsedOrderId)) {
            stopCamera()
            setScannerOpen(false)
            void openOrder(parsedOrderId)
            return
          }
        } catch {
          // Continue below; some QR codes can be URLs.
        }

        if (!result.startsWith('http')) {
          return
        }

        try {
          const url = new URL(result)
          const parts = url.pathname.split('/')
          const orderIndex = parts.indexOf('orders')
          const parsedOrderId = Number(parts[orderIndex + 1])

          if (Number.isFinite(parsedOrderId)) {
            stopCamera()
            setScannerOpen(false)
            void openOrder(parsedOrderId)
          }
        } catch {
          // Ignore invalid URL payloads and keep scanning.
        }
      }, 300)
    } catch (err: any) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Camera permission denied. Please allow access.')
      } else {
        setCameraError(`Unable to access camera: ${err.message || 'Unknown camera error'}`)
      }
    }
  }, [openOrder, stopCamera])

  useEffect(() => {
    if (scannerOpen) {
      void startScanner()
    } else {
      stopCamera()
    }

    return () => stopCamera()
  }, [scannerOpen, startScanner, stopCamera])

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
  })

  const fetchOrders = useCallback(
    async (page = 1, pageSize = pagination.pageSize) => {
      setLoading(true)

      try {
        const params: {
          status?: string
          start_date?: string
          end_date?: string
          page?: number
          per_page?: number
        } = {
          page,
          per_page: pageSize,
        }

        if (statusFilter) {
          params.status = statusFilter
        }

        if (dateRange) {
          params.start_date = dateRange[0].format('YYYY-MM-DD')
          params.end_date = dateRange[1].format('YYYY-MM-DD')
        }

        const response = await sellerOrdersAPI.list(params)

        const payload = response.data?.data as LaravelPaginator<Order>

        const orderRows = Array.isArray(payload?.data)
          ? (payload.data as Order[])
          : []

        setOrders(orderRows)

        setPagination({
          current: payload.current_page || 1,
          pageSize: payload.per_page || pageSize,
          total: payload.total || 0,
        })

        setSummary(normalizeSummary(response.data?.summary, orderRows))

        setApiDateRange(
          (response.data?.date_range as DateRangePayload | undefined) ?? null,
        )
      } catch (error: any) {
        message.error(error.response?.data?.message || 'Failed to load orders')

        setOrders([])
        setSummary(emptySummary)
        setApiDateRange(null)
      } finally {
        setLoading(false)
      }
    },
    [statusFilter, dateRange, message, pagination.pageSize],
  )

  const downloadOrdersCsv = async () => {
    try {
      const params = dateRange
        ? {
          start_date: dateRange[0].format('YYYY-MM-DD'),
          end_date: dateRange[1].format('YYYY-MM-DD'),
          status: csvStatusFilter,
        }
        : { status: csvStatusFilter }

      const response = await sellerOrdersAPI.exportOrdersCsv(params)
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `seller-orders-${csvStatusFilter}-${dayjs().format('YYYYMMDD_HHmmss')}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      message.success('Orders CSV downloaded.')
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to download orders CSV')
    }
  }

  useEffect(() => {
    void fetchOrders()
  }, [fetchOrders])

  const resetPackedModal = () => {
    setPackedModalOpen(false)
    setPackedPhotoFiles([])
    setPendingPackedOrderId(null)
    setPendingPackedViaScan(false)
  }

  const submitPackedStatus = async () => {
    const files = packedPhotoFiles
      .map((file) => file.originFileObj)
      .filter((file): file is File => file instanceof File)

    if (files.length < PACKAGE_PHOTO_LABELS.length) {
      message.warning(`Upload all ${PACKAGE_PHOTO_LABELS.length} package photos: ${PACKAGE_PHOTO_LABELS.join(', ')}`)
      return
    }

    const labels = PACKAGE_PHOTO_LABELS.slice(0, files.length)

    setSaving(true)
    try {
      if (pendingPackedViaScan && scannedBarcode) {
        const response = await sellerOrdersAPI.scanUpdateStatus(scannedBarcode, 'packed', files, [...labels])
        setSelectedOrder(response.data.data)
        setScanUpdateOpen(false)
        setScannedBarcode(null)
        message.success('Order marked as packed via barcode scan')
      } else if (pendingPackedOrderId) {
        const response = await sellerOrdersAPI.updateStatus(pendingPackedOrderId, 'packed', files, [...labels])
        setSelectedOrder(response.data.data)
        message.success('Order marked as packed')
      }

      resetPackedModal()
      await fetchOrders()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update order to packed')
    } finally {
      setSaving(false)
    }
  }

  const updateStatus = async (status: string) => {
    if (!selectedOrder) {
      return
    }

    if (!canUpdateOrderStatus) {
      message.warning('You are not permitted to update order status.')
      return
    }

    if (status === 'packed') {
      setPendingPackedOrderId(selectedOrder.id)
      setPendingPackedViaScan(false)
      setPackedPhotoFiles([])
      setPackedModalOpen(true)
      return
    }

    setSaving(true)
    try {
      const response = await sellerOrdersAPI.updateStatus(selectedOrder.id, status)
      setSelectedOrder(response.data.data)
      message.success('Order status updated')
      await fetchOrders()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update order status')
    } finally {
      setSaving(false)
    }
  }

  const submitScanStatusUpdate = async () => {
    if (!scannedBarcode || !selectedOrder) {
      return
    }

    if (!canUpdateOrderStatus) {
      message.warning('You are not permitted to update order status.')
      return
    }

    if (scanTargetStatus === 'packed') {
      setPendingPackedOrderId(selectedOrder.id)
      setPendingPackedViaScan(true)
      setPackedPhotoFiles([])
      setPackedModalOpen(true)
      return
    }

    setSaving(true)
    try {
      const response = await sellerOrdersAPI.scanUpdateStatus(scannedBarcode, scanTargetStatus)
      setSelectedOrder(response.data.data)
      setScanUpdateOpen(false)
      setScannedBarcode(null)
      message.success('Order status updated via barcode scan')
      await fetchOrders()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update order status')
    } finally {
      setSaving(false)
    }
  }

  const lookupOrderByNumber = async (orderNumber: string) => {
    setSaving(true)
    try {
      const response = await sellerOrdersAPI.lookupByBarcode(orderNumber)
      stopCamera()
      setScannerOpen(false)
      setSelectedOrder(response.data.data)
      setScannedBarcode(orderNumber)
      setScanTargetStatus('processing')
      setScanUpdateOpen(true)
    } catch {
      message.warning('Order not found. Check the order number and try again.')
    } finally {
      setSaving(false)
    }
  }

  const startBarcodeReaderListening = useCallback(() => {
    setBarcodeReaderOpen(true)
    setBarcodeReaderListening(true)
    barcodeBufferRef.current = ''
    barcodeLastKeyAtRef.current = 0

    if (barcodeArmTimeoutRef.current) {
      clearTimeout(barcodeArmTimeoutRef.current)
    }

    barcodeArmTimeoutRef.current = setTimeout(() => {
      if (barcodeReaderListening) {
        message.warning('No barcode reader found. Please attach barcode reader and try again.')
        stopBarcodeReaderListening()
      }
    }, 8000)
  }, [barcodeReaderListening, message, stopBarcodeReaderListening])

  useEffect(() => {
    if (!barcodeReaderListening) {
      return
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey || event.altKey) {
        return
      }

      const now = Date.now()
      const delta = now - barcodeLastKeyAtRef.current
      barcodeLastKeyAtRef.current = now

      if (delta > 180) {
        barcodeBufferRef.current = ''
      }

      if (event.key === 'Enter') {
        const scannedValue = barcodeBufferRef.current.trim()
        barcodeBufferRef.current = ''

        if (!scannedValue) {
          return
        }

        if (barcodeArmTimeoutRef.current) {
          clearTimeout(barcodeArmTimeoutRef.current)
          barcodeArmTimeoutRef.current = null
        }

        stopBarcodeReaderListening()
        void lookupOrderByNumber(scannedValue)
        return
      }

      if (event.key.length === 1) {
        barcodeBufferRef.current += event.key
      }
    }

    window.addEventListener('keydown', onKeyDown)

    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [barcodeReaderListening, stopBarcodeReaderListening])

  const columns: ColumnsType<Order> = [
    {
      title: 'Order',
      key: 'order_number',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>#{record.order_number}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{new Date(record.created_at).toLocaleString()}</div>
        </div>
      ),
    },
    {
      title: 'Customer',
      key: 'customer',
      render: (_, record) => record.customer?.name ?? 'Guest customer',
    },
    {
      title: 'Items',
      key: 'items',
      render: (_, record) => record.items?.length ?? 0,
    },
    {
      title: 'Total',
      key: 'total_amount',
      render: (_, record) => formatCurrency(toNumber(record.total_amount)),
    },
    {
      title: 'Payment',
      key: 'payment_status',
      render: (_, record) => <Tag color={paymentStatusColors[record.payment_status] ?? 'default'}>{formatLabel(record.payment_status)}</Tag>,
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => <Tag color={statusColors[record.status] ?? 'default'}>{formatLabel(record.status)}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} onClick={() => void openOrder(record.id)}>
            View
          </Button>
          <Button icon={<FileTextOutlined />} onClick={() => void downloadOrderMemo(record.id, record.order_number)}>
            Memo
          </Button>
        </Space>
      ),
    },
  ]

  const currentRange = {
    start: dateRange ? dateRange[0].format('YYYY-MM-DD') : apiDateRange?.start_date ?? null,
    end: dateRange ? dateRange[1].format('YYYY-MM-DD') : apiDateRange?.end_date ?? null,
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Orders Management</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>Download order memos, scan barcodes for status updates, and track fulfillment.</div>
        </div>
        <Space wrap>
          <Select
            allowClear
            placeholder="Filter by status"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 180 }}
            options={statusOptions.map((status) => ({ label: formatLabel(status), value: status }))}
          />
          <DatePicker.RangePicker
            value={dateRange}
            format="YYYY-MM-DD"
            allowClear
            onChange={(dates) => {
              if (!dates) {
                setDateRange(null)
                return
              }

              if (dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]])
              }
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void fetchOrders()}>
            Refresh
          </Button>
          <Select
            value={csvStatusFilter}
            onChange={setCsvStatusFilter}
            style={{ width: 160 }}
            options={csvStatusOptions.map((status) => ({ label: status === 'all' ? 'All Orders' : formatLabel(status), value: status }))}
          />
          <Button icon={<DownloadOutlined />} onClick={() => void downloadOrdersCsv()}>
            Download CSV
          </Button>
          <Button icon={<ScanOutlined />} onClick={() => setScannerOpen(true)} style={{ background: '#0f766e', borderColor: '#0f766e', color: '#fff' }}>
            Scan QR
          </Button>
          <Button icon={<ScanOutlined />} onClick={() => startBarcodeReaderListening()} style={{ background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' }}>
            Scan Barcode Reader
          </Button>
        </Space>
      </div>

      <div style={{ marginBottom: 12 }}>
        <Typography.Text type="secondary">
          Analytics Range: {formatDateLabel(currentRange.start)} to {formatDateLabel(currentRange.end)}
        </Typography.Text>
      </div>

      <div className="seller-card-grid" style={{ marginBottom: 20 }}>
        <Card>
          <Statistic title="Total Orders" value={summary.total_orders} />
        </Card>
        <Card>
          <Statistic
            title="Delivered"
            value={summary.delivered}
            styles={{ content: { color: '#15803d' } }}
          />
        </Card>
        <Card>
          <Statistic
            title="Shipped"
            value={summary.shipped}
            styles={{ content: { color: '#0891b2' } }}
          />
        </Card>
        <Card>
          <Statistic
            title="Pending"
            value={summary.pending}
            styles={{ content: { color: '#b45309' } }}
          />
        </Card>
        <Card>
          <Statistic
            title="Cancelled"
            value={summary.cancelled}
            styles={{ content: { color: '#b91c1c' } }}
          />
        </Card>
        <Card>
          <Statistic title={`Revenue (${activeCurrency})`} value={summary.revenue} precision={2} formatter={(value) => formatCurrency(Number(value ?? 0))} />
          <Typography.Text type="secondary">Today: {formatCurrency(summary.today_revenue)}</Typography.Text>
        </Card>
      </div>

      <Table
        columns={columns}
        dataSource={orders}
        rowKey="id"
        loading={loading}
        scroll={{ x: 960 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} orders`,
          onChange: (page, pageSize) => {
            fetchOrders(page, pageSize)
          },
        }}
      />

      <Modal title={selectedOrder ? `Order #${selectedOrder.order_number}` : 'Order'} open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} width={920}>
        {selectedOrder ? (
          <Space orientation="vertical" size="large" style={{ width: '100%' }}>
            <Card>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Customer">{selectedOrder.customer?.name ?? 'Guest customer'}</Descriptions.Item>
                <Descriptions.Item label="Email">{selectedOrder.customer?.email ?? 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Payment Status"><Tag color={paymentStatusColors[selectedOrder.payment_status] ?? 'default'}>{formatLabel(selectedOrder.payment_status)}</Tag></Descriptions.Item>
                <Descriptions.Item label="Total">{formatCurrency(toNumber(selectedOrder.total_amount))}</Descriptions.Item>
                <Descriptions.Item label="Tracking ID">{selectedOrder.tracking_id || selectedOrder.tracking_number || 'Not assigned'}</Descriptions.Item>
                <Descriptions.Item label="Created At">{new Date(selectedOrder.created_at).toLocaleString()}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              title="Order Memo"
              extra={
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => void downloadOrderMemo(selectedOrder.id, selectedOrder.order_number)}
                >
                  Download Order Memo
                </Button>
              }
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Print the order memo and attach it to the package. The memo includes ship-from and ship-to addresses,
                a QR code for order details, and a barcode for status updates at warehouse, shop, or delivery.
              </Typography.Paragraph>
            </Card>

            <Card title="Update Status">
              <Select
                value={selectedOrder.status}
                style={{ width: 220 }}
                onChange={(value) => void updateStatus(value)}
                loading={saving}
                disabled={!canUpdateOrderStatus}
                options={statusOptions.filter((status) => status !== 'pending').map((status) => ({ label: formatLabel(status), value: status }))}
              />
              {!canUpdateOrderStatus ? (
                <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  You have read-only order access.
                </Typography.Text>
              ) : null}
              {selectedOrder.status === 'packed' || (selectedOrder.package_images?.length ?? 0) > 0 ? (
                <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  Packed orders require front, back, side, and memo-facing package photos uploaded to storage.
                </Typography.Text>
              ) : null}
            </Card>

            {(selectedOrder.package_images?.length ?? 0) > 0 ? (
              <Card title="Package Photos">
                <Image.PreviewGroup>
                  <Space wrap>
                    {selectedOrder.package_images?.map((image) => (
                      <div key={image.id} style={{ textAlign: 'center' }}>
                        <Image
                          src={image.url ?? undefined}
                          alt={image.label ?? 'Package photo'}
                          width={120}
                          height={120}
                          style={{ objectFit: 'cover', borderRadius: 8 }}
                        />
                        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 11 }}>
                          {formatLabel(image.label ?? 'photo')}
                        </Typography.Text>
                      </div>
                    ))}
                  </Space>
                </Image.PreviewGroup>
              </Card>
            ) : null}

            <Card title="Order Items">
              <Table
                rowKey="id"
                pagination={true}
                dataSource={selectedOrder.items ?? []}
                columns={[
                  { title: 'Product', dataIndex: 'product_name', key: 'product_name' },
                  {
                    title: (
                      <span style={iconLabelStyle}>
                        <i className="bi bi-upc-scan" aria-hidden="true" />
                        <span>SKU</span>
                      </span>
                    ),
                    dataIndex: 'product_sku',
                    key: 'product_sku',
                    render: (value: string) => (
                      <span style={iconValueStyle}>
                        <i className="bi bi-upc-scan" aria-hidden="true" />
                        <span>{value || 'N/A'}</span>
                      </span>
                    ),
                  },
                  { title: 'Qty', dataIndex: 'quantity', key: 'quantity' },
                  { title: 'Unit Price', key: 'unit_price', render: (_, item: OrderItem) => formatCurrency(toNumber(item.unit_price)) },
                  { title: 'Line Total', key: 'total_price', render: (_, item: OrderItem) => formatCurrency(toNumber(item.total_price)) },
                ]}
              />
            </Card>

            <Card title="Shipping Address">
              {(() => {
                const shipping = formatOrderShippingAddress(selectedOrder)

                if (shipping.lines.length === 0 && !shipping.name && !shipping.contact) {
                  return <Typography.Text type="secondary">No shipping address available</Typography.Text>
                }

                return (
                  <Space orientation="vertical" size={6} style={{ width: '100%' }}>
                    {shipping.name ? <Typography.Text strong>{shipping.name}</Typography.Text> : null}
                    {shipping.contact ? <Typography.Text type="secondary">{shipping.contact}</Typography.Text> : null}
                    {shipping.lines.map((line, index) => (
                      <Typography.Text key={`${line}-${index}`} style={{ display: 'block' }}>
                        {line}
                      </Typography.Text>
                    ))}
                    {shipping.notes ? (
                      <Typography.Text type="secondary">Notes: {shipping.notes}</Typography.Text>
                    ) : null}
                  </Space>
                )
              })()}
            </Card>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="Scan Order QR Code"
        open={scannerOpen}
        onCancel={() => setScannerOpen(false)}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <div style={{ textAlign: 'center' }}>
          {cameraError ? (
            <div style={{ marginBottom: 16 }}>
              <CameraOutlined style={{ fontSize: 32, color: '#b91c1c', marginBottom: 8 }} />
              <Typography.Text type="danger" style={{ display: 'block', marginBottom: 12 }}>{cameraError}</Typography.Text>
              <Button onClick={() => { setCameraError(null); void startScanner() }}>Retry</Button>
            </div>
          ) : null}
          <div style={{ position: 'relative', width: '100%', maxWidth: 460, margin: '0 auto', borderRadius: 12, overflow: 'hidden', background: '#000', minHeight: 240 }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', display: 'block', minHeight: 240 }}
            />
            <div style={{ position: 'absolute', inset: '20%', border: '2px dashed rgba(15,118,110,0.7)', borderRadius: 12, pointerEvents: 'none' }} />
          </div>
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            Point your camera at an order QR code
          </Typography.Text>
          <div style={{ marginTop: 16 }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Or enter order number manually:
            </Typography.Text>
            <Space style={{ marginTop: 8 }}>
              <input
                id="manual-order-input"
                placeholder="e.g. TRN-20260421-ABCD1234"
                style={{ padding: '6px 12px', border: '1px solid #d9d9d9', borderRadius: 8, width: 240 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const val = (e.target as HTMLInputElement).value.trim()
                    if (val) {
                      void lookupOrderByNumber(val)
                    }
                  }
                }}
              />
              <Button type="primary" size="small" onClick={() => {
                const input = document.getElementById('manual-order-input') as HTMLInputElement
                const val = input?.value?.trim()
                if (val) {
                  void lookupOrderByNumber(val)
                }
              }}>Go</Button>
            </Space>
          </div>
        </div>
      </Modal>

      <Modal
        title="Scan Barcode Reader"
        open={barcodeReaderOpen}
        onCancel={stopBarcodeReaderListening}
        footer={null}
        width={520}
        destroyOnHidden
      >
        <div style={{ textAlign: 'center', padding: '12px 0' }}>
          <ScanOutlined style={{ fontSize: 40, color: '#1d4ed8', marginBottom: 12 }} />
          <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>
            Waiting for barcode reader input
          </Typography.Text>
          <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Please scan the order barcode using your attached laser scanner.
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            If no device is attached, this window will close with a warning.
          </Typography.Text>
        </div>
      </Modal>

      <Modal
        title={selectedOrder ? `Barcode Update — #${selectedOrder.order_number}` : 'Barcode Update'}
        open={scanUpdateOpen}
        onCancel={() => {
          setScanUpdateOpen(false)
          setScannedBarcode(null)
        }}
        onOk={() => void submitScanStatusUpdate()}
        okText="Update Status"
        confirmLoading={saving}
        okButtonProps={{ disabled: !canUpdateOrderStatus }}
      >
        {selectedOrder ? (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Text>
              Scanned barcode: <Typography.Text code>{scannedBarcode}</Typography.Text>
            </Typography.Text>
            <Typography.Text type="secondary">
              Current status: <Tag color={statusColors[selectedOrder.status] ?? 'default'}>{formatLabel(selectedOrder.status)}</Tag>
            </Typography.Text>
            <div>
              <Typography.Text style={{ display: 'block', marginBottom: 8 }}>New status</Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={scanTargetStatus}
                onChange={setScanTargetStatus}
                options={statusOptions
                  .filter((status) => !['pending', 'cancelled'].includes(status))
                  .map((status) => ({ label: formatLabel(status), value: status }))}
              />
            </div>
            {scanTargetStatus === 'packed' ? (
              <Typography.Text type="warning">
                Marking as packed requires uploading front, back, side, and memo-facing package photos.
              </Typography.Text>
            ) : null}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title="Upload Package Photos (Required for Packed)"
        open={packedModalOpen}
        onCancel={resetPackedModal}
        onOk={() => void submitPackedStatus()}
        okText="Mark as Packed"
        confirmLoading={saving}
      >
        <Typography.Paragraph type="secondary">
          Upload clear photos of the package: front, back, side, and memo facing. Images are stored in S3 under the order folder.
        </Typography.Paragraph>
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          {PACKAGE_PHOTO_LABELS.map((label) => {
            const existing = packedPhotoFiles.find((file) => file.name === label)

            return (
              <div key={label}>
                <Typography.Text strong style={{ display: 'block', marginBottom: 8, textTransform: 'capitalize' }}>
                  {label} view
                </Typography.Text>
                <Upload
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  maxCount={1}
                  listType="picture-card"
                  fileList={existing ? [existing] : []}
                  beforeUpload={(file) => {
                    const nextFile: UploadFile = {
                      uid: label,
                      name: label,
                      status: 'done',
                      originFileObj: file,
                    }

                    setPackedPhotoFiles((current) => [
                      ...current.filter((entry) => entry.name !== label),
                      nextFile,
                    ])

                    return false
                  }}
                  onRemove={() => {
                    setPackedPhotoFiles((current) => current.filter((entry) => entry.name !== label))
                  }}
                >
                  {!existing ? '+ Upload' : null}
                </Upload>
              </div>
            )
          })}
        </Space>
      </Modal>
    </div>
  )
}

export default OrdersPage
